import React from 'react';

import '../../App.css';

// i18n
import { useTranslation } from 'react-i18next';

// Routes
import { useHistory } from 'react-router-dom';

// redux
import {useDispatch, useSelector} from '../../reduxHooks';

// Components
import styled from 'styled-components';
import { Button } from '@betareader/design-system';
import Icon from '../../components/AppIcon';

import firebase from '../../config/fire';

import { colorModes, loggedOut, updateUserProfile } from '../../modules/user';

import { resetActiveAccount } from '../../modules/account';

import Avatar from '../../components/avatar/avatar';

import { authUtil, featureToggleUtil, productCapUtil } from '../../utils';
import SupportModal from '../../components/menu/supportModal';
import { useHostAppProxy } from 'src/contexts/HostAppProxyContext';
import {
  canOpenMcpIntegrationSettings,
  isMcpFeatureAvailable,
} from '../integrations/integrations';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

const UserDropdownWrapper = styled.details`
  position: relative;
  height: 40px;
`;

const Trigger = styled.summary`
  display: flex;
  align-items: center;
  min-height: 40px;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker { display: none; }
  &:focus-visible { outline: 2px solid var(--br-focus); outline-offset: 2px; }
`;

const MenuPanel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 100;
  min-width: 220px;
  max-width: min(300px, calc(100vw - 24px));
  padding: 6px;
  border: 1px solid var(--br-border);
  border-radius: var(--br-card-radius);
  background: var(--br-surface);
  color: var(--br-text);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.15);
`;

const MenuHeader = styled.div`
  padding: 8px 10px 4px;
  color: var(--br-muted);
  font-size: 0.75rem;
  font-weight: 700;
`;

const MenuItem = styled(Button)`
  width: 100%;
  justify-content: flex-start;
  min-height: 36px;
  padding: 6px 10px;
  text-align: left;
`;

const MenuDivider = styled.hr`
  border: 0;
  border-top: 1px solid var(--br-border);
  margin: 5px 0;
`;

const UserDropdown: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    signOut: signOutByProxy,
    settings: { showPaymentInfo, isWrapped },
  } = useHostAppProxy();

  const [showSupportModal, setShowSupportModal] = React.useState(false);
  const menuRef = React.useRef<HTMLDetailsElement>(null);

  React.useEffect(() => {
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        menuRef.current.open = false;
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, []);

  const selectItem = (action: () => void | Promise<void>) => () => {
    if (menuRef.current) {
      menuRef.current.open = false;
      menuRef.current.querySelector('summary')?.focus();
    }
    action();
  };

  const userProfile = useSelector((state: any) => state.user.userProfile);
  const activeAccount = useSelector(
    (state: any) => state.account.activeAccount
  );
  const { flags, isLoading: flagsLoading } = useFeatureFlags();

  const handleSignout = async () => {
    if ((userProfile.accountMembership?.length ?? 0) > 1) {
      // if this user has several accounts, just disable the active one
      // so they can choose to switch account instead of signing out completely
      dispatch(resetActiveAccount());
    } else {
      dispatch(resetActiveAccount());
      dispatch(loggedOut());
      await signOutByProxy();
      firebase.auth().signOut();
    }
  };

  const handleSignIn = () => {
    history.push('/signin');
  };

  const openSupportConversation = () => {
    setShowSupportModal(false);
    history.push('/inbox/conversations/support');
  };

  const openSupportModal = () => setShowSupportModal(true);

  const toggleColorMode = async () => {
    if (!userProfile) {
      return;
    }
    const currentIndex = colorModes.findIndex(
      mode => mode === userProfile.readerSettings.colorMode
    );
    const nextIndex = (currentIndex + 1) % colorModes.length;
    const nextColorMode = colorModes[nextIndex];
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateUserProfile(idToken, {
        readerSettings: {
          ...userProfile.readerSettings,
          colorMode: nextColorMode,
        },
      })
    );
  };

  const canEditAccount = productCapUtil.canEditAccount({
    activeAccount,
    userProfile,
  });
  const canOpenMcpIntegrations =
    isMcpFeatureAvailable(flags, flagsLoading, userProfile) &&
    canOpenMcpIntegrationSettings(userProfile, activeAccount);
  const canShowPaymentInfo = featureToggleUtil.shouldShowPaymentInfo(
    userProfile,
    { showPaymentInfo, isWrapped },
    flags?.ios_iap === true
  );

  return (
    <>
      <SupportModal
        onClose={() => setShowSupportModal(false)}
        open={showSupportModal}
        onStartSupportChat={openSupportConversation}
        signedIn={!!userProfile}
      />
      {isWrapped && !userProfile ? (
        <span role="status" aria-busy="true">Loading account…</span>
      ) : (
        <UserDropdownWrapper
          ref={menuRef}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.preventDefault();
              if (menuRef.current) menuRef.current.open = false;
              menuRef.current?.querySelector('summary')?.focus();
            }
          }}
        >
          <Trigger>
            {userProfile ? (
              <Avatar
                name={userProfile.displayName}
                src={userProfile.photoUrl || '/assets/images/BR-square.png'}
              />
            ) : t('SignIn')}
          </Trigger>
          <MenuPanel>
            {userProfile && (
              <>
                <MenuHeader>{t('UserInfo')}</MenuHeader>
                <MenuItem variant="ghost" size="small" onClick={selectItem(() => history.push('/account/profile'))}>
                  <Icon name="user" aria-hidden="true" />{t('Profile')}
                </MenuItem>
                <MenuItem variant="ghost" size="small" onClick={selectItem(() => history.push('/account/settings'))}>
                  <Icon name="settings" aria-hidden="true" />{t('Settings')}
                </MenuItem>
                <MenuItem variant="ghost" size="small" onClick={selectItem(toggleColorMode)}>
                  <Icon name="lightbulb" aria-hidden="true" />{t('ColorMode')}
                </MenuItem>
                {canEditAccount && <MenuHeader>{t('Account')}</MenuHeader>}
                {canEditAccount && (
                  <MenuItem variant="ghost" size="small" onClick={selectItem(() => history.push('/account/members'))}>
                    <Icon name="users" aria-hidden="true" />{t('Members')}
                  </MenuItem>
                )}
                {canEditAccount && canShowPaymentInfo && (
                  <MenuItem variant="ghost" size="small" onClick={selectItem(() => history.push('/account/subscription'))}>
                    <Icon name="payment" aria-hidden="true" />{t('Subscription')}
                  </MenuItem>
                )}
                {canOpenMcpIntegrations && (
                  <MenuItem variant="ghost" size="small" onClick={selectItem(() => history.push('/account/integrations'))}>
                    <Icon name="plug" aria-hidden="true" />{t('McpIntegrations', 'Integrations')}
                  </MenuItem>
                )}
                <MenuDivider />
                {userProfile.maxReadersPerBook > -1 && (
                  <MenuItem variant="ghost" size="small" onClick={selectItem(() => history.push('/account/reader-bonus'))}>
                    <Icon name="certificate" aria-hidden="true" />{t('GetMoreReaders')}
                  </MenuItem>
                )}
              </>
            )}
            <MenuItem variant="ghost" size="small" onClick={selectItem(openSupportModal)}>
              <Icon name="help circle" aria-hidden="true" />{t('Support')}
            </MenuItem>
            <MenuItem variant="ghost" size="small" onClick={selectItem(userProfile ? handleSignout : handleSignIn)}>
              <Icon name={userProfile ? 'log out' : 'sign-in'} aria-hidden="true" />
              {userProfile
                ? (userProfile.accountMembership?.length ?? 0) > 1
                  ? t('SwitchAccount')
                  : t('SignOut')
                : t('SignIn')}
            </MenuItem>
          </MenuPanel>
        </UserDropdownWrapper>
      )}
    </>
  );
};

export default UserDropdown;
